import {SupportedAqForecastModelList} from './aqConstants';
import {SupportedForecastModelList} from './constants';
import ForecastModelListHandler from './ForecastModelListHandler';
import {
  AqForecastMetadata,
  ForecastModelConfig,
  NormalizedAqForecastModel,
  NormalizedForecastModel,
} from './types';

export const getForecastModelList = (rawModelList: ForecastModelConfig[]) => {
  const modelList = SupportedForecastModelList.flatMap((formattedModel) => {
    const matchingRows = rawModelList.filter(
      (rawModel) =>
        rawModel.forecast_model === formattedModel.serverId.forecastModel &&
        (formattedModel.serverId.meshSize === null ||
          rawModel.mesh_size === formattedModel.serverId.meshSize),
    );
    if (matchingRows.length === 0) {
      return [];
    }
    const defaultForUser = matchingRows.some((row) => row.default_for_user);
    const coverageImageLight =
      matchingRows.find((row) => row.coverage_image_light)
        ?.coverage_image_light ?? null;
    const coverageImageDark =
      matchingRows.find((row) => row.coverage_image_dark)
        ?.coverage_image_dark ?? null;
    return [
      {
        ...formattedModel,
        defaultForUser,
        coverageImageLight,
        coverageImageDark,
      },
    ];
  });

  return new ForecastModelListHandler(modelList);
};

// Reorders a list of forecast models using the user's saved preference.
// `savedOrder` is keyed by backend server name (e.g. "ARPEGE") and one entry
// can map to multiple mesh-size variants in the list (ARPEGE_EU, ARPEGE_GLOBAL).
// Models not present in `savedOrder` keep their original relative order and
// are appended after the ordered ones.
export const orderForecastModelsByPreference = (
  models: NormalizedForecastModel[],
  savedOrder: string[] | null,
): NormalizedForecastModel[] => {
  if (!savedOrder || savedOrder.length === 0) return models;
  const ordered: NormalizedForecastModel[] = [];
  const used = new Set<number>();
  for (const name of savedOrder) {
    models.forEach((m, idx) => {
      if (m.serverId.forecastModel === name && !used.has(idx)) {
        ordered.push(m);
        used.add(idx);
      }
    });
  }
  models.forEach((m, idx) => {
    if (!used.has(idx)) ordered.push(m);
  });
  return ordered;
};

/**
 * Resolve an air quality response's `metadata` to a catalogue entry.
 *
 * Matches on the model name *and* the mesh: CAMS Global and CAMS Europe share
 * the family name and differ only by grid, so a name-only match would resolve
 * 40 km data to the 10 km entry and print the wrong resolution.
 *
 * Returns `undefined` — rather than a placeholder — for anything the catalogue
 * does not know, so the caller can keep printing the raw name instead of
 * rendering an empty source line.
 */
export const getAqForecastModelInfo = (
  metadata?: AqForecastMetadata | null,
): NormalizedAqForecastModel | undefined => {
  if (!metadata?.forecast_model) return undefined;

  return SupportedAqForecastModelList.find(
    (model) =>
      model.serverId.forecastModel === metadata.forecast_model &&
      model.serverId.meshSize === metadata.mesh_size,
  );
};
